import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Snackbar, Alert, Typography } from '@mui/material';
import type { WorkItem } from '../workitems/workItemTypes';
import type { PrintLabelResult } from '@techgitter/contracts';
import {
  printInvoiceDocument,
  printLabel,
  printRepairTicketLabel,
  printTicketSheet,
} from '../../services/printApi';
import { LABEL_LANE_COUNTER, LABEL_LANE_WORKSHOP } from '../../config/printLanes';
import { buildWorkItemCustomerLabelRequest } from '../../utils/workItemCustomerLabel';
import { createQuote, fetchQuotes } from '../../services/quotesApi';
import { formatQuoteServiceTotal } from '../../utils/quoteServiceTotal';
import type { QuotesListItem, QuoteStatus, QuoteType } from '@techgitter/contracts';
import { useJobLogs } from '../../hooks/useJobLogs';
import { useJobCalls } from '../../hooks/useJobCalls';
import { callToLogEntry, interleaveByTime } from './jobLogEntries';
import { useCustomerServices } from '../../hooks/useCustomerServices';
import { mergeLogsWithComments } from '../workitems/utils/mergeLogsWithComments';
import { hasDiagnosticsLog } from '../workitems/utils/diagnosticsLog';
import { NoDiagnosticsQuoteDialog } from '../workitems/components/NoDiagnosticsQuoteDialog';
import { formatCustomerDisplay, formatSmartDate } from '../workitems/utils/workItemUtils';
import { initialsFromName, isAppointmentItem } from './workItemToRow';
import { useUserColors } from '../../hooks/useUserColors';
import { SYSTEM_AVATAR_COLOR } from '../../styles/avatarPalette';
import { WorkItemPanel, type LogEntry, type LogSubmitType, type PrintAction } from './WorkItemPanel';
import { LineItemsSection } from './LineItemsSection';
import { TaskListSection } from './TaskListSection';
import { CheckInPanelSection } from './CheckInPanelSection';
import { JobDeviceSection } from './JobDeviceSection';
import { AddCustomerServiceModal } from '../tickets/Customer/AddCustomerServiceModal';
import { CredentialRequestDialog } from '../credentials/CredentialRequestDialog';
import { useCredentialRequests } from '../../hooks/useCredentialRequests';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import { getCustomerIndexCached } from '../../services/customerIndexCacheService';

const RS_BASE = 'https://techsolvers.repairshopr.com';

/**
 * Who may raise a credential request. Mirrors CREDENTIAL_ROLES in sm4rt-api —
 * Accounts and Purchasing are absent because a request is part of doing the
 * work on a ticket, not part of billing for it. The API is authoritative; this
 * only avoids offering a button that would 403.
 */
const CREDENTIAL_ROLES = ['Admin', 'Manager', 'Senior Technician', 'Technician'] as const;

/** Display labels for SM4RT-native JobLog types. */
const TYPE_LABELS: Record<string, string> = {
  comment: 'Comment',
  contact: 'Call log',
  update: 'Update',
  diagnostics: 'Diagnosis',
  standup: 'Standup',
};

/** Quote status → chip colour tone (mirrors the legacy expansion panel). */
const QUOTE_STATUS_TONE: Record<
  QuoteStatus,
  'default' | 'primary' | 'info' | 'success' | 'error' | 'warning'
> = {
  requested: 'primary',
  draft: 'default',
  sent: 'info',
  accepted: 'success',
  declined: 'error',
  expired: 'warning',
};

/** Quote type → human label (mirrors the legacy expansion panel). */
const QUOTE_TYPE_LABEL: Record<QuoteType, string> = {
  new_laptop: 'New Laptop',
  new_desktop: 'New Desktop',
  new_desktop_custom: 'New Desktop (Custom)',
  new_all_in_one: 'New All-in-One',
  repair: 'Repair',
  network_install: 'Network Install',
  m365: 'M365',
  replacement: 'New Computer',
};

interface WorkItemPanelContainerProps {
  workItem: WorkItem;
  userName: string;
  onWorkItemUpdated: (id: string, updates: Partial<WorkItem>) => void;
  /** Open the full check-in session view (check-in items only). */
  onOpenCheckInSession?: (sessionId: string) => void;
  /** Cancel/delete the work item (check-in items route to the cancel dialog). */
  onDelete?: (workItem: WorkItem) => Promise<void>;
  /** Reassign the work item's customer (job/ticket items only). */
  onChangeCustomer?: (item: WorkItem) => void;
  /** Admin gate — enables the "add customer service" affordance. */
  isAdmin?: boolean;
}

/**
 * Wires a WorkItem into the presentational WorkItemPanel: customer fields from
 * the item, services from useCustomerServices, and the job log (read + submit)
 * from useJobLogs merged with RepairShopr comments. The spec's `'call'` log type
 * maps to the JobLogType `'contact'`.
 */
export const WorkItemPanelContainer: React.FC<WorkItemPanelContainerProps> = ({
  workItem,
  userName,
  onWorkItemUpdated,
  onOpenCheckInSession,
  onDelete,
  onChangeCustomer,
  isAdmin,
}) => {
  const navigate = useNavigate();
  // Logs bind to a job when there is one (job items, and appointments linked to
  // a job), and to the appointment itself only for standalone appointments
  // (which have no job). For job items `guid === jobGUID`.
  const linkedJobGuid = workItem.jobGUID ?? workItem.linkedJobGuid;
  const isStandaloneAppointment = workItem.type === 'appointment' && !linkedJobGuid;
  const logParent = isStandaloneAppointment ? 'appointment' : 'job';
  const logGuid = isStandaloneAppointment ? workItem.guid : (linkedJobGuid ?? workItem.guid);
  const { logs, isSubmitting, addLog } = useJobLogs(logGuid, userName, logParent);
  // 3CX call records linked to the job, interleaved into the job log. Calls
  // bind to a job, never to an appointment, so a standalone appointment (and a
  // check-in row, which has no job yet) fetches nothing.
  const { data: jobCalls, error: jobCallsError } = useJobCalls(linkedJobGuid);
  // Secure credential intake. A request binds to a RepairShopr ticket — that
  // binding is what the ticket-close purge keys on — so items without one
  // (standalone appointments, check-in rows) get no button at all.
  const currentUser = useCurrentUser();
  const rsTicketId = workItem.type === 'job' ? String(workItem.id || '') : '';
  const canIssueCredentials = !!currentUser?.hasAnyRole(...CREDENTIAL_ROLES);
  const showCredentials = canIssueCredentials && !!rsTicketId && rsTicketId !== '0';
  const {
    requests: credentialRequests,
    liveRequest: liveCredentialRequest,
    isLoading: credentialsLoading,
    error: credentialsError,
    refresh: refreshCredentials,
  } = useCredentialRequests(showCredentials ? rsTicketId : '');
  const [credentialsOpen, setCredentialsOpen] = useState(false);
  // The customer reads this on the submission page and it becomes the
  // 1Password item title, so the ticket number rides inside it (keeping the
  // vault searchable by ticket) and it stays editable in the dialog.
  const credentialDefaultLabel = useMemo(() => {
    const subject = (workItem.title || '').trim();
    const number = workItem.number ? ` (#${workItem.number})` : '';
    return subject ? `${subject}${number}` : `Ticket${number || ` ${workItem.id}`}`;
  }, [workItem.title, workItem.number, workItem.id]);
  const { services, create: createService } = useCustomerServices(workItem.customerId || null);
  // Admin-only "add service" affordance (re-instated from the legacy panel).
  const [addServiceModalOpen, setAddServiceModalOpen] = useState(false);
  const canAddService = !!isAdmin && !!workItem.customerId;
  const { colorFor } = useUserColors();
  // RepairShopr customer id, resolved from the DV customer id via the cached
  // customer index — workItem.customerId is the Dataverse GUID, but the RS
  // shortcut URLs below need the RepairShopr customer id.
  const [rsCustomerId, setRsCustomerId] = useState<number>(0);
  useEffect(() => {
    const dvId = workItem.customerId;
    if (!dvId) {
      setRsCustomerId(0);
      return;
    }
    let cancelled = false;
    getCustomerIndexCached()
      .then((result) => {
        if (cancelled) return;
        const match = result.items.find((c) => c.dataverseCustomerId === dvId);
        setRsCustomerId(Number(match?.repairShoprCustomerId) || 0);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [workItem.customerId]);
  const [creatingQuote, setCreatingQuote] = useState(false);
  // Quotes linked to this job (fetched by jobGUID). The create-quote flow
  // optimistically prepends the new quote to avoid a round-trip.
  const [quotes, setQuotes] = useState<QuotesListItem[]>([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  // Gate: warn before quoting a job with no Diagnostics job-log.
  const [noDiagnosticsPromptOpen, setNoDiagnosticsPromptOpen] = useState(false);
  // Bumped to tell the panel to focus its log input and switch to the
  // Diagnostics comment type when the tech declines the prompt.
  const [focusDiagnosticsSignal, setFocusDiagnosticsSignal] = useState(0);
  // Bumped by the panel's Line items "+" to add a new line item in the editor.
  const [addLineItemSignal, setAddLineItemSignal] = useState(0);
  // Bumped by the panel's Line items Sync icon to re-sync from RepairShopr.
  const [syncLineItemSignal, setSyncLineItemSignal] = useState(0);
  // Live count + total reported by the editor, shown in the Line items header.
  const [lineItemsSummary, setLineItemsSummary] = useState<{ count: number; total: number } | null>(null);
  // Bumped by the panel's Task list "+" to open the task-list picker.
  const [addTaskListSignal, setAddTaskListSignal] = useState(0);
  // Live task progress reported by the task-list editor, shown in its header.
  const [taskListSummary, setTaskListSummary] = useState<{ total: number; closed: number } | null>(null);

  const logEntries = useMemo<LogEntry[]>(() => {
    const timed = mergeLogsWithComments(logs, workItem.recentComments).map((e) => ({
      at: e.kind === 'log' ? e.log.createdAt : e.createdAt,
      entry: e.kind === 'log'
        ? {
            id: `l-${e.log.id}`,
            authorInitials: initialsFromName(e.log.createdBy || 'SM4RT'),
            authorId: e.log.userId || e.log.createdBy || '',
            // Authorless SM4RT logs (shown as "SM4RT") are system-generated —
            // render them in the reserved system grey rather than a hashed
            // palette colour. Named system actors are caught by colorFor.
            authorColor:
              !e.log.userId && !e.log.createdBy
                ? SYSTEM_AVATAR_COLOR
                : colorFor({ id: e.log.userId, name: e.log.createdBy }),
            source: 'sm4rt' as const,
            typeLabel: TYPE_LABELS[e.log.type] ?? 'Note',
            formattedTime: formatSmartDate(e.log.createdAt),
            text: e.log.text,
          }
        : {
            id: `c-${e.id}`,
            authorInitials: initialsFromName(e.createdBy || 'RS'),
            authorId: e.createdBy || '',
            authorColor: colorFor({ name: e.createdBy }),
            source: 'repairshopr' as const,
            // RS comments carry their label (subject) from the RS API.
            typeLabel: e.subject || 'Note',
            formattedTime: formatSmartDate(e.createdAt),
            text: e.text,
          },
    }));
    return interleaveByTime([
      ...timed,
      ...jobCalls.map((call) => callToLogEntry(call, colorFor)),
    ]);
  }, [logs, workItem.recentComments, jobCalls, colorFor]);

  const [smsMessage, setSmsMessage] = useState<string | null>(null);

  const handleLogSubmit = async (text: string, type: LogSubmitType) => {
    await addLog({ type: type === 'call' ? 'contact' : type, text });

    if (type === 'call' && text === 'Customers phone not in service') {
      const phone = (workItem.customerMobile || workItem.customerPhone || '<phone>').replace(/\s/g, '');
      const customerFirstName = (workItem.customerName || '').split(' ')[0];
      const userFirstName = userName.split(' ')[0];
      setSmsMessage(
        `Please send the following email from the service mailbox to ${phone}@sms.clicksend.com :\n\n` +
        `Hi ${customerFirstName}, ${userFirstName} @TechSolvers here, we tried calling but couldn't reach you, please call us on 02 8502 8954 for an update on your job.`,
      );
    }
  };

  const [printingAction, setPrintingAction] = useState<PrintAction | null>(null);
  const [snack, setSnack] = useState<{
    open: boolean;
    severity: 'success' | 'error' | 'warning';
    message: string;
  }>({ open: false, severity: 'success', message: '' });

  // Only jobs carry a RepairShopr ticket, and both the job label and the ticket
  // sheet are addressed by it. `id` is "0"/empty on appointments and check-in
  // rows, which would resolve to no work item server-side.
  const rsTicketRef = workItem.type === 'job' ? String(workItem.id || '') : '';
  const hasRsTicket = !!rsTicketRef && rsTicketRef !== '0';

  // The invoice to print. RepairShopr ids increase monotonically, so the
  // highest is the most recently raised — a safer "latest" than the display
  // number, which is a string whose prefix rolls over each year.
  const latestInvoice = useMemo(() => {
    const invoices = workItem.invoices ?? [];
    if (invoices.length === 0) return null;
    return [...invoices].sort((a, b) => b.id - a.id)[0];
  }, [workItem.invoices]);

  const printUnavailable = useMemo<Partial<Record<PrintAction, string>>>(() => {
    const reasons: Partial<Record<PrintAction, string>> = {};
    if (!hasRsTicket) {
      const noTicket = 'No RepairShopr ticket on this item';
      reasons['job-label-upstairs'] = noTicket;
      reasons['job-label-downstairs'] = noTicket;
      reasons['ticket-sheet'] = noTicket;
    }
    if (!(workItem.customerBusinessName || workItem.customerName)) {
      reasons['customer-label'] = 'No customer on this item';
    }
    if (!latestInvoice) {
      reasons.invoice = 'No invoice has been raised on this ticket yet';
    }
    return reasons;
  }, [hasRsTicket, workItem.customerBusinessName, workItem.customerName, latestInvoice]);

  /**
   * Run one print and report it. Every path resolves to a `PrintLabelResult`,
   * whose `skipped` flag is NOT a failure — it means sm4rt-api has no Service
   * Bus configured (local dev), and saying "sent to printer" there would have
   * the operator standing at a printer that is never going to move.
   */
  const runPrint = async (
    action: PrintAction,
    what: string,
    send: () => Promise<PrintLabelResult>,
  ) => {
    setPrintingAction(action);
    try {
      const result = await send();
      if (result.skipped) {
        setSnack({
          open: true,
          severity: 'warning',
          message: `Print skipped: ${result.reason ?? 'printer not configured'}`,
        });
      } else {
        setSnack({
          open: true,
          severity: 'success',
          message: `${what} sent to printer${result.jobId ? ` (job ${result.jobId.slice(0, 8)})` : ''}`,
        });
      }
    } catch (err) {
      setSnack({
        open: true,
        severity: 'error',
        message: err instanceof Error ? err.message : 'Print failed',
      });
    } finally {
      setPrintingAction(null);
    }
  };

  const handlePrint = (action: PrintAction) => {
    switch (action) {
      case 'job-label-upstairs':
        return runPrint(action, 'Job label', () =>
          printRepairTicketLabel(workItem.id, { role: LABEL_LANE_WORKSHOP }),
        );
      case 'job-label-downstairs':
        return runPrint(action, 'Job label', () =>
          printRepairTicketLabel(workItem.id, { role: LABEL_LANE_COUNTER }),
        );
      case 'customer-label': {
        const built = buildWorkItemCustomerLabelRequest(workItem, {
          rsCustomerId,
          origin: window.location.origin,
        });
        if (!built.ok) {
          setSnack({ open: true, severity: 'error', message: built.error });
          return;
        }
        return runPrint(action, 'Customer label', () => printLabel(built.request));
      }
      case 'invoice': {
        if (!latestInvoice) return;
        return runPrint(action, `Invoice ${latestInvoice.number}`, () =>
          printInvoiceDocument(latestInvoice.number),
        );
      }
      case 'ticket-sheet':
        return runPrint(action, 'Ticket sheet', () => printTicketSheet(workItem.id));
    }
  };

  // Fetch quotes linked to this job. Re-fetches when the linked job changes
  // (e.g. after an appointment is linked to a new job). Standalone items with
  // no job GUID have no quotes.
  useEffect(() => {
    if (!workItem.jobGUID) {
      setQuotes([]);
      return;
    }
    const controller = new AbortController();
    setQuotesLoading(true);
    fetchQuotes({
      page: 1,
      pageSize: 50,
      jobId: workItem.jobGUID,
      signal: controller.signal,
    })
      .then((resp) => setQuotes(resp.items))
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        setSnack({
          open: true,
          severity: 'error',
          message: err instanceof Error ? err.message : 'Failed to load quotes',
        });
      })
      .finally(() => setQuotesLoading(false));
    return () => controller.abort();
  }, [workItem.jobGUID]);

  // A failed call fetch would otherwise leave a job log that silently lacks
  // the calls — say so, the way a failed quotes fetch does.
  useEffect(() => {
    if (!jobCallsError) return;
    setSnack({ open: true, severity: 'error', message: `Couldn't load calls for this job: ${jobCallsError}` });
  }, [jobCallsError]);

  // Map the fetched quotes to the panel's display shape.
  const quotePanelItems = useMemo(
    () =>
      quotes.map((q) => ({
        id: q.id,
        number: q.number,
        name: q.name,
        totalLabel: formatQuoteServiceTotal(q),
        typeLabel: QUOTE_TYPE_LABEL[q.type] ?? q.type,
        status: q.status,
        statusTone: QUOTE_STATUS_TONE[q.status] ?? 'default',
      })),
    [quotes],
  );

  // Mirrors WorkItemExpansionPanel.handleCreateQuote: create a repair quote
  // server-side prefilled from the job (customer + device + Diagnostics
  // job-log), then navigate to it. Line items aren't sent — the backend seeds
  // the consolidated description from the latest Diagnostics entry.
  const doCreateQuote = async () => {
    if (creatingQuote) return;
    if (!workItem.customerId) {
      setSnack({
        open: true,
        severity: 'error',
        message: 'Ticket has no linked customer — cannot create a quote',
      });
      return;
    }
    setCreatingQuote(true);
    try {
      // A repair quote's deviceType drives the repair-service-type catalog,
      // which only covers laptop / desktop / all-in-one; other check-in
      // device types are left unset (their inspection still flows through
      // via sourceCheckInDraftJobId).
      const rawDeviceType = workItem.checkInDraftJobs?.[0]?.deviceType;
      const checkInDeviceType =
        rawDeviceType === 'laptop' ||
        rawDeviceType === 'desktop' ||
        rawDeviceType === 'all_in_one'
          ? rawDeviceType
          : undefined;
      // Auto-link to a specific device only when there's exactly one — with
      // 2+ devices we leave it null so the tech picks on the quote page.
      const sourceCheckInDraftJobId =
        workItem.checkInDraftJobs?.length === 1
          ? workItem.checkInDraftJobs[0].id
          : undefined;
      const result = await createQuote({
        customerId: workItem.customerId,
        sm4rtJobId: workItem.jobGUID,
        type: 'repair',
        repairServices: [],
        deviceType: checkInDeviceType,
        sourceCheckInDraftJobId,
      });
      // Prepend so it shows immediately if the tech navigates back. QuoteDetail
      // extends QuotesListItem, so the shape is compatible.
      setQuotes((prev) => [result, ...prev]);
      navigate(`/quote/${result.number}`);
    } catch (e) {
      setSnack({
        open: true,
        severity: 'error',
        message: e instanceof Error ? e.message : 'Failed to create quote',
      });
    } finally {
      setCreatingQuote(false);
    }
  };

  // Add Quote entry point: a repair quote's description is seeded from the
  // latest Diagnostics job-log, so warn first when none has been posted.
  const handleCreateQuote = () => {
    if (creatingQuote) return;
    if (hasDiagnosticsLog(logs)) {
      void doCreateQuote();
      return;
    }
    setNoDiagnosticsPromptOpen(true);
  };

  const handleConfirmQuoteWithoutDiagnostics = () => {
    setNoDiagnosticsPromptOpen(false);
    void doCreateQuote();
  };

  const handleDeclineQuoteWithoutDiagnostics = () => {
    setNoDiagnosticsPromptOpen(false);
    setFocusDiagnosticsSignal((n) => n + 1);
  };

  // Appointments with no linked RepairShopr customer have no useful RS links
  // (Customer/Invoices/Tickets all point nowhere) — hide the whole RepairShop
  // Links section for them. The Print section is unaffected: it is addressed by
  // the work item, not by an RS customer id.
  const showShortcuts = !(isAppointmentItem(workItem) && !rsCustomerId);

  const lineItemsSummaryText = lineItemsSummary
    ? lineItemsSummary.count === 0
      ? 'No line items'
      : `${lineItemsSummary.count} item${lineItemsSummary.count === 1 ? '' : 's'} · $${lineItemsSummary.total.toFixed(2)}`
    : undefined;

  const taskListSummaryText = taskListSummary
    ? taskListSummary.total === 0
      ? 'No task lists'
      : `${taskListSummary.closed} of ${taskListSummary.total}`
    : undefined;

  return (
    <>
    {smsMessage && (
      <Alert severity="info" sx={{ mb: 1 }} onClose={() => setSmsMessage(null)}>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
          {smsMessage}
        </Typography>
      </Alert>
    )}
    {workItem.type === 'check-in' && (
      <CheckInPanelSection
        workItem={workItem}
        onOpenCheckInSession={onOpenCheckInSession}
        onDelete={onDelete}
        onWorkItemUpdated={onWorkItemUpdated}
      />
    )}
    <WorkItemPanel
      customerName={formatCustomerDisplay(workItem)}
      // `customerId` is the Dataverse customer GUID — one of the two
      // identifiers /customer/:id resolves (the other being the RepairShopr
      // id). Appointments can arrive without one, so the link is conditional.
      customerHref={workItem.customerId ? `/customer/${workItem.customerId}` : undefined}
      customerEmail={workItem.customerEmail || null}
      customerMobile={workItem.customerMobile || workItem.customerPhone || null}
      customerAddress={workItem.address || null}
      customerServices={services.map((s) => s.displayName)}
      canAddService={canAddService}
      onAddService={() => setAddServiceModalOpen(true)}
      onChangeCustomer={
        workItem.type === 'job' && onChangeCustomer
          ? () => onChangeCustomer(workItem)
          : undefined
      }
      customerNotes={null}
      repairshoprCustomerUrl={`${RS_BASE}/customers/${rsCustomerId}`}
      repairshoprInvoicesUrl={`${RS_BASE}/invoices?search%5Bcustomer_id%5D=${rsCustomerId}`}
      repairshoprTicketsUrl={`${RS_BASE}/tickets/${workItem.id}`}
      showShortcuts={showShortcuts}
      onPrint={handlePrint}
      printUnavailable={printUnavailable}
      printingAction={printingAction}
      deviceSection={
        workItem.type !== 'check-in' && (workItem.checkInDraftJobs?.length ?? 0) > 0 ? (
          <JobDeviceSection workItem={workItem} onOpenCheckInSession={onOpenCheckInSession} />
        ) : undefined
      }
      onOpenCredentials={showCredentials ? () => setCredentialsOpen(true) : undefined}
      credentialsLabel={
        liveCredentialRequest ? 'View credential request' : 'Create credential request'
      }
      logEntries={logEntries}
      onLogSubmit={handleLogSubmit}
      isSubmitting={isSubmitting}
      quotes={quotePanelItems}
      quotesLoading={quotesLoading}
      onOpenQuote={(quoteNumber) => navigate(`/quote/${quoteNumber}`)}
      onCreateQuote={handleCreateQuote}
      isCreatingQuote={creatingQuote}
      focusDiagnosticsSignal={focusDiagnosticsSignal}
      onAddLineItem={() => setAddLineItemSignal((n) => n + 1)}
      onSyncLineItems={() => setSyncLineItemSignal((n) => n + 1)}
      canSyncLineItems={!!workItem.linkedTicketId}
      lineItemsSummary={lineItemsSummaryText}
      renderLineItems={() => (
        <LineItemsSection
          workItem={workItem}
          onWorkItemUpdated={onWorkItemUpdated}
          addSignal={addLineItemSignal}
          syncSignal={syncLineItemSignal}
          onSummaryChange={setLineItemsSummary}
          rsCustomerId={rsCustomerId}
        />
      )}
      onAddTaskList={() => setAddTaskListSignal((n) => n + 1)}
      taskListSummary={taskListSummaryText}
      renderTaskList={() => (
        <TaskListSection
          workItem={workItem}
          userName={userName}
          onWorkItemUpdated={onWorkItemUpdated}
          addSignal={addTaskListSignal}
          onSummaryChange={setTaskListSummary}
        />
      )}
    />
    <Snackbar
      open={snack.open}
      autoHideDuration={4000}
      onClose={() => setSnack((s) => ({ ...s, open: false }))}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      <Alert
        severity={snack.severity}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        sx={{ width: '100%' }}
      >
        {snack.message}
      </Alert>
    </Snackbar>
    <NoDiagnosticsQuoteDialog
      open={noDiagnosticsPromptOpen}
      onCancel={handleDeclineQuoteWithoutDiagnostics}
      onContinue={handleConfirmQuoteWithoutDiagnostics}
    />
    {showCredentials && (
      <CredentialRequestDialog
        open={credentialsOpen}
        onClose={() => setCredentialsOpen(false)}
        requests={credentialRequests}
        liveRequest={liveCredentialRequest}
        isLoading={credentialsLoading}
        loadError={credentialsError}
        onRefresh={refreshCredentials}
        rsTicketId={rsTicketId}
        defaultLabel={credentialDefaultLabel}
        customerEmail={workItem.customerEmail || ''}
        customerMobile={workItem.customerMobile || workItem.customerPhone || ''}
        onLog={(text) => { void addLog({ type: 'update', text }); }}
      />
    )}
    {isAdmin && (
      <AddCustomerServiceModal
        open={addServiceModalOpen}
        onClose={() => setAddServiceModalOpen(false)}
        onSubmit={async (payload) => { await createService(payload); }}
        existingServiceIds={services.map((s) => s.serviceId)}
        customerName={formatCustomerDisplay(workItem)}
      />
    )}
    </>
  );
};
